import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    Body2,
    Box,
    Flex,
    HorizontalDivider,
    IconChevronRight,
    OutlinedCard,
    Stack,
    Subtitle1,
    Subtitle2,
    TextButton,
} from '@myphx-engineering/phx-design-system';
import { useActivityConfig } from '@phx/myphx-lib';

import { ConditionalComponent } from '../../../components/utility/ConditionalComponent';
import { getFragment } from '../../../graphql';
import {
    type DigitallyReceivedPrescriberOrderInfoFragment,
    PrescriberOrderStatus,
    type ProviderOffersInfoFragment,
    RenewalStatus,
} from '../../../graphql/generated/graphql';
import { useFlags } from '../../../hooks/use-flags';
import {
    formatToLocalizedDate,
    getAbsoluteRoute,
    titleCase,
} from '../../../util';

export type LastOrderActivityV2Props = {
    offer?: ProviderOffersInfoFragment;
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
};

export const LastOrderActivityV2 = ({
    offer,
    prescriberOrder,
}: LastOrderActivityV2Props) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { enableRxCancelMyphx, enableRxRenewalMyphx } = useFlags();

    const pharmacy = offer
        ? getFragment(getFragment(offer.provider))
        : undefined;

    let pharmacyName;
    switch (pharmacy?.__typename) {
        case 'ChainProvider':
            pharmacyName = pharmacy.chainName;
            break;
        case 'MailOrderProvider':
        case 'PharmacyProvider':
            pharmacyName = pharmacy.name;
            break;
        default:
            pharmacyName = undefined;
    }

    const navigateToOrderConfirmation = useCallback(() => {
        navigate(
            getAbsoluteRoute('orders.prescriberOrderId.confirmation', {
                prescriberOrderId: prescriberOrder.id,
            })
        );
    }, [navigate, prescriberOrder.id]);

    const navigateToRenewalStatus = useCallback(() => {
        navigate(
            getAbsoluteRoute('orders.prescriberOrderId.renewalStatus', {
                prescriberOrderId: prescriberOrder.id,
            })
        );
    }, [navigate, prescriberOrder.id]);

    const activityConfigs = useActivityConfig({
        prescriberOrder,
        navigateToRenewalStatus,
        navigateToOrderConfirmation,
        pharmacyName: pharmacyName ?? null,
        enableRxCancel: enableRxCancelMyphx,
        enableRxRenewal: enableRxRenewalMyphx,
        titleCase,
        RenewalStatus,
        PrescriberOrderStatus,
    });

    return (
        <ConditionalComponent
            condition={
                prescriberOrder.status === PrescriberOrderStatus.Cancelled ||
                !!prescriberOrder.latestSelectedOffer
            }
        >
            <OutlinedCard>
                <Stack gap="xs">
                    <Subtitle1>{t('orderDetails.lastActivity')}</Subtitle1>
                    <HorizontalDivider />
                    <Stack gap="xs">
                        {activityConfigs.map((activityConfig, index) => (
                            <Stack key={index} gap="xxxs">
                                <Flex gap="xs" justify="space-between">
                                    <Subtitle2>
                                        {activityConfig.label}
                                    </Subtitle2>
                                    {activityConfig.timestamp && (
                                        <Body2>
                                            {formatToLocalizedDate(
                                                new Date(
                                                    activityConfig.timestamp
                                                ),
                                                {
                                                    dateStyle: 'medium',
                                                    timeZone:
                                                        activityConfig.timeZone,
                                                },
                                                i18n.language
                                            )}
                                        </Body2>
                                    )}
                                </Flex>
                                {activityConfig.additionalContent}
                                {activityConfig.action && (
                                    <Box>
                                        <TextButton
                                            RightIcon={IconChevronRight}
                                            onClick={
                                                activityConfig.action.onClick
                                            }
                                        >
                                            {activityConfig.action.label}
                                        </TextButton>
                                    </Box>
                                )}
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            </OutlinedCard>
        </ConditionalComponent>
    );
};
